import React, { useCallback, useEffect, useState } from "react";
import { useWatch } from "react-hook-form";
import dataProvider from "server/dataProvider";
import { InternshipPlanScheduleTypes } from "utils/constants";
import { graduationRequests } from "api/graduationApi";
import { Grid } from "@mui/material";
import { ArrayInput, FormDataConsumer, ReferenceInput, required, SelectInput, SimpleFormIterator } from "react-admin";
import { InternshipSessionInput } from "server/components/Fields/InternshipSessionInput";
import CustomDateInput from "server/components/Fields/CustomDateInput";
import { validateEnd, validateStart } from "utils/validations";
import { settings } from "app";
import CustomTimeOnlyInput from "server/components/Fields/CustomTimeOnlyInput";
import sessionExamsHelper from "components/AcademicYears/Exams/helper";
import AcademicYearInput from "components/LearningActivity/CreateWeeklySchedule/components/Inputs/AcademicYearInput";
import { administrationRequests } from "api/administrationApi";

const CreateForm = ({ filters }) => {
  const [internshipSessionFromDate, setInternshipSessionFromDate] = useState(null);
  const [internshipSessionToDate, setInternshipSessionToDate] = useState(null);

  const formAcademicYearId = useWatch({ name: "academicYearId" });
  const formStructuralUnitId = useWatch({ name: "structuralUnitId" });
  const formInternshipSessionId = useWatch({ name: "internshipSessionId" });
  const formPlanId = useWatch({ name: "planId" });
  const fromDate = useWatch({ name: "fromDate" });
  const toDate = useWatch({ name: "toDate" });

  const academicYearId = formAcademicYearId ?? filters?.academicYearId;
  const structuralUnitId = formStructuralUnitId ?? filters?.structuralUnitId;
  const internshipSessionId = formInternshipSessionId ?? filters?.internshipSessionId;
  const planId = formPlanId ?? filters?.formPlanId;

  const fetchInternshipSessionsDate = useCallback(async () => {
    try {
      const { data } = await dataProvider.customRequest(
        graduationRequests.getInternshipSession(internshipSessionId),
        null,
        "GET",
      );
      setInternshipSessionFromDate(data?.startDate);
      setInternshipSessionToDate(data?.endDate);
    } catch (err) {
      console.error(err);
    }
  }, [internshipSessionId]);

  useEffect(() => {
    if (!internshipSessionId) return;
    fetchInternshipSessionsDate();
  }, [internshipSessionId, fetchInternshipSessionsDate]);

  return (
    <>
      <Grid
        container
        spacing={2}
      >
        <Grid
          item
          xs={12}
          md={6}
        >
          <AcademicYearInput withoutLanguage />
        </Grid>
        <Grid
          item
          xs={12}
          md={6}
        >
          <ReferenceInput
            source="structuralUnitId"
            reference={administrationRequests.getStructuralUnitsSelect()}
            filter={{ filterByUser: true }}
          >
            <SelectInput
              label="Структурна единица"
              optionText="name"
              validate={required()}
            />
          </ReferenceInput>
        </Grid>
        {structuralUnitId && (
          <>
            <Grid
              item
              xs={12}
              md={6}
            >
              <InternshipSessionInput
                key={structuralUnitId}
                filter={{ structuralUnitId, academicYearId }}
              />
            </Grid>

            <Grid
              item
              xs={12}
              md={6}
            >
              <ReferenceInput
                source="planId"
                reference={graduationRequests.getInternshipPlansSelect()}
              >
                <SelectInput
                  label="Стажантски план"
                  optionText="name"
                  emptyText={false}
                />
              </ReferenceInput>
            </Grid>
          </>
        )}
        {planId && (
          <Grid
            item
            xs={12}
            md={6}
          >
            <ReferenceInput
              source="disciplineId"
              reference={graduationRequests.getPlanDisciplinesSelect(planId)}
              filter={{ planScheduleType: InternshipPlanScheduleTypes.IndividualCycles }}
            >
              <SelectInput
                label="Дисциплина"
                validate={required()}
                optionText="nameBg"
              />
            </ReferenceInput>
          </Grid>
        )}
        <Grid
          item
          xs={12}
          md={3}
        >
          <CustomDateInput
            source="fromDate"
            label="Начална дата"
            minDate={internshipSessionFromDate}
            maxDate={internshipSessionToDate}
            validate={() => validateStart(fromDate, toDate, required)}
            noTime
          />
        </Grid>
        <Grid
          item
          xs={12}
          md={3}
        >
          <CustomDateInput
            source="toDate"
            label="Крайна дата"
            minDate={internshipSessionFromDate}
            maxDate={internshipSessionToDate}
            validate={() => validateEnd(toDate, fromDate, required)}
            noTime
          />
        </Grid>
      </Grid>
      <ArrayInput
        source="slots"
        label="Времеви интервал"
        defaultValue={[{}]}
      >
        <SimpleFormIterator
          disableReordering
          disableClear
          inline
          sx={settings.style.iteratorWithoutGap}
        >
          <FormDataConsumer>
            {({ scopedFormData }) => (
              <Grid
                container
                spacing={2}
              >
                <Grid
                  item
                  xs={12}
                  md={3}
                >
                  <CustomTimeOnlyInput
                    source="startTime"
                    label="Начало"
                    required={true}
                    validate={() =>
                      sessionExamsHelper.validateTimeSlots({
                        startTime: scopedFormData?.startTime,
                        endTime: scopedFormData?.endTime,
                      })
                    }
                  />
                </Grid>
                <Grid
                  item
                  xs={12}
                  md={3}
                >
                  <CustomTimeOnlyInput
                    source="endTime"
                    label="Край"
                    required={true}
                    validate={() =>
                      sessionExamsHelper.validateTimeSlots({
                        startTime: scopedFormData?.startTime,
                        endTime: scopedFormData?.endTime,
                      })
                    }
                  />
                </Grid>
              </Grid>
            )}
          </FormDataConsumer>
        </SimpleFormIterator>
      </ArrayInput>
    </>
  );
};

export default CreateForm;
